import { z } from 'zod';
import { ulid } from 'ulid';

/**
 * Opaque, prefixed, ULID-based identifiers (§28.1 "Opaque IDs").
 * Format: `<prefix>_<26-char Crockford base32 ULID>`.
 * ULIDs are lexicographically sortable by creation time, which suits DynamoDB sort keys.
 */

// Crockford base32 alphabet used by ULID (excludes I, L, O, U).
const ULID_BODY = '[0-9A-HJKMNP-TV-Z]{26}';

export interface IdCodec<B extends string> {
  readonly prefix: string;
  readonly schema: z.ZodType<z.BRAND<B> & string, z.ZodTypeDef, unknown>;
  generate(): z.BRAND<B> & string;
  is(value: unknown): value is z.BRAND<B> & string;
}

function makeId<B extends string>(prefix: string): IdCodec<B> {
  const re = new RegExp(`^${prefix}_${ULID_BODY}$`);
  const schema = z
    .string()
    .regex(re, `Invalid ${prefix} identifier`)
    .brand<B>();
  return {
    prefix,
    schema: schema as unknown as IdCodec<B>['schema'],
    generate: () => `${prefix}_${ulid()}` as z.BRAND<B> & string,
    is: (value: unknown): value is z.BRAND<B> & string =>
      typeof value === 'string' && re.test(value),
  };
}

export const ids = {
  workspace: makeId<'WorkspaceId'>('ws'),
  user: makeId<'UserId'>('user'),
  artist: makeId<'ArtistId'>('artist'),
  template: makeId<'TemplateId'>('tmpl'),
  gig: makeId<'GigId'>('gig'),
  liveSession: makeId<'LiveSessionId'>('live'),
  song: makeId<'SongId'>('song'),
  setlist: makeId<'SetlistId'>('setl'),
  setlistItem: makeId<'SetlistItemId'>('sitem'),
  audienceSession: makeId<'AudienceSessionId'>('as'),
  interaction: makeId<'InteractionId'>('int'),
  brief: makeId<'BriefId'>('brief'),
  improvProfile: makeId<'ImprovProfileId'>('improv'),
  rhythmCatalog: makeId<'RhythmCatalogId'>('rcat'),
  catalogEntry: makeId<'CatalogEntryId'>('rhythm'),
  duplicateGroup: makeId<'DuplicateGroupId'>('grp'),
  notificationChannel: makeId<'ChannelId'>('chan'),
  notificationDelivery: makeId<'DeliveryId'>('deliv'),
  paymentConnection: makeId<'PaymentConnectionId'>('paycon'),
  tip: makeId<'TipId'>('tip'),
  auditEvent: makeId<'AuditEventId'>('audit'),
  domainEvent: makeId<'DomainEventId'>('evt'),
  request: makeId<'RequestId'>('req'),
} as const;

export const WorkspaceId = ids.workspace.schema;
export type WorkspaceId = z.infer<typeof WorkspaceId>;
export const UserId = ids.user.schema;
export type UserId = z.infer<typeof UserId>;
export const ArtistId = ids.artist.schema;
export type ArtistId = z.infer<typeof ArtistId>;
export const TemplateId = ids.template.schema;
export type TemplateId = z.infer<typeof TemplateId>;
export const GigId = ids.gig.schema;
export type GigId = z.infer<typeof GigId>;
export const LiveSessionId = ids.liveSession.schema;
export type LiveSessionId = z.infer<typeof LiveSessionId>;
export const SongId = ids.song.schema;
export type SongId = z.infer<typeof SongId>;
export const SetlistId = ids.setlist.schema;
export type SetlistId = z.infer<typeof SetlistId>;
export const SetlistItemId = ids.setlistItem.schema;
export type SetlistItemId = z.infer<typeof SetlistItemId>;
export const AudienceSessionId = ids.audienceSession.schema;
export type AudienceSessionId = z.infer<typeof AudienceSessionId>;
export const InteractionId = ids.interaction.schema;
export type InteractionId = z.infer<typeof InteractionId>;
export const BriefId = ids.brief.schema;
export type BriefId = z.infer<typeof BriefId>;
export const ImprovProfileId = ids.improvProfile.schema;
export type ImprovProfileId = z.infer<typeof ImprovProfileId>;
export const RhythmCatalogId = ids.rhythmCatalog.schema;
export type RhythmCatalogId = z.infer<typeof RhythmCatalogId>;
export const CatalogEntryId = ids.catalogEntry.schema;
export type CatalogEntryId = z.infer<typeof CatalogEntryId>;
export const DuplicateGroupId = ids.duplicateGroup.schema;
export type DuplicateGroupId = z.infer<typeof DuplicateGroupId>;
export const ChannelId = ids.notificationChannel.schema;
export type ChannelId = z.infer<typeof ChannelId>;
export const DeliveryId = ids.notificationDelivery.schema;
export type DeliveryId = z.infer<typeof DeliveryId>;
export const PaymentConnectionId = ids.paymentConnection.schema;
export type PaymentConnectionId = z.infer<typeof PaymentConnectionId>;
export const TipId = ids.tip.schema;
export type TipId = z.infer<typeof TipId>;
export const AuditEventId = ids.auditEvent.schema;
export type AuditEventId = z.infer<typeof AuditEventId>;
export const DomainEventId = ids.domainEvent.schema;
export type DomainEventId = z.infer<typeof DomainEventId>;
export const RequestId = ids.request.schema;
export type RequestId = z.infer<typeof RequestId>;
